/**
 * Диаграмма Ганта собственной разработки (ПЛ-25): таблица работ и шкала от часов до кварталов,
 * базовый план под фактом, критический путь, резервы, связи, вехи, линия даты отчёта; перетаскивание
 * работ (ограничение «начало не ранее»), длительности и связей. Отрисовываются только видимые строки
 * и видимая часть шкалы — 10 000 работ листаются без задержек (ПЛ-11).
 */
import {
  type ActivityRow,
  dayNumber,
  dayString,
  parseMoment,
  type ScheduleOp,
  WorkCalendar,
} from '@epc/contracts';
import clsx from 'clsx';
import { ChevronDown, ChevronRight, Lock } from 'lucide-react';
import {
  type PointerEvent as RPointerEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  barDates,
  calDef,
  fmtDur,
  fmtMoment,
  hpd,
  momentX,
  type Row,
  type View,
  ZOOMS,
  type ZoomKey,
} from './model';

const HEADER = 48;
const MONTHS = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];

export function useIsPhone(): boolean {
  const [phone, setPhone] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches,
  );
  useEffect(() => {
    const m = window.matchMedia('(max-width: 767px)');
    const on = () => setPhone(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, []);
  return phone;
}

type Drag =
  | { kind: 'move'; id: string; x0: number; dx: number }
  | { kind: 'resize'; id: string; x0: number; dx: number }
  | { kind: 'link'; id: string; x0: number; y0: number; x: number; y: number };

export function Gantt({
  view,
  rows,
  zoom,
  selectedId,
  onSelect,
  onOpen,
  onToggle,
  onEdit,
  canEdit,
  showBaseline,
  showLinks,
  height,
}: {
  view: View;
  rows: Row[];
  zoom: ZoomKey;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onOpen: (id: string) => void;
  onToggle: (id: string) => void;
  onEdit: (ops: ScheduleOp[], label: string) => void;
  canEdit: boolean;
  showBaseline: boolean;
  showLinks: boolean;
  height: string;
}) {
  const phone = useIsPhone();
  // На телефоне строка 45 px: 1 px занимает линия, остаётся 44 px на нажатие
  const RH = phone ? 45 : 32;
  const pxDay = ZOOMS.find((z) => z.key === zoom)!.pxDay;
  const cols = phone
    ? [{ key: 'name', title: 'Работа', w: 168 }]
    : [
        { key: 'code', title: 'Код', w: 76 },
        { key: 'name', title: 'Работа', w: 300 },
        { key: 'dur', title: 'Длит.', w: 60 },
        { key: 'start', title: 'Начало', w: 78 },
        { key: 'finish', title: 'Окончание', w: 84 },
        { key: 'tf', title: 'Резерв', w: 62 },
      ];
  const leftW = cols.reduce((s, c) => s + c.w, 0);

  // Шкала: от самого раннего до самого позднего момента графика с запасом
  const { originDay, days } = useMemo(() => {
    let lo = dayNumber(view.schedule.startDate);
    let hi = lo + 30;
    for (const a of view.activities) {
      const { start, finish } = barDates(a);
      if (start) lo = Math.min(lo, dayNumber(start));
      if (finish) hi = Math.max(hi, dayNumber(finish));
    }
    for (const d of Object.values(view.baselineDates)) {
      lo = Math.min(lo, dayNumber(d[0]));
      hi = Math.max(hi, dayNumber(d[1]));
    }
    if (view.schedule.dataDate) lo = Math.min(lo, dayNumber(view.schedule.dataDate));
    const pad = zoom === 'hours' ? 2 : zoom === 'days' ? 7 : zoom === 'weeks' ? 21 : 60;
    // Неделя с понедельника — ровная сетка
    const from = lo - pad - ((((lo - pad + 3) % 7) + 7) % 7);
    return { originDay: from, days: hi - from + pad * 2 };
  }, [view, zoom]);
  const timelineW = Math.max(400, Math.ceil(days * pxDay));
  const cal = useMemo(() => {
    const d = calDef(view, null);
    return d ? new WorkCalendar(d, originDay, days + 1) : null;
  }, [view, originDay, days]);
  const calFor = useMemo(() => {
    const cache = new Map<string, WorkCalendar>();
    return (id: string | null) => {
      const key = id ?? '';
      let c = cache.get(key);
      if (!c) {
        const d = calDef(view, id);
        if (!d) return cal;
        c = new WorkCalendar(d, originDay - 400, days + 4000);
        cache.set(key, c);
      }
      return c;
    };
  }, [view, cal, originDay, days]);

  const scroller = useRef<HTMLElement>(null);
  const [scroll, setScroll] = useState({ top: 0, left: 0, w: 1000, h: 600 });
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const upd = () =>
      setScroll({ top: el.scrollTop, left: el.scrollLeft, w: el.clientWidth, h: el.clientHeight });
    upd();
    el.addEventListener('scroll', upd, { passive: true });
    const ro = new ResizeObserver(upd);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', upd);
      ro.disconnect();
    };
  }, []);
  // Первый показ — к дате отчёта или началу работ
  const scrolledFor = useRef<string>('');
  useEffect(() => {
    const el = scroller.current;
    const key = `${view.schedule.id}:${zoom}`;
    if (!el || scrolledFor.current === key) return;
    scrolledFor.current = key;
    const anchor =
      view.schedule.dataDate ??
      view.activities
        .map((a) => barDates(a).start)
        .filter(Boolean)
        .sort()[0] ??
      view.schedule.startDate;
    // Чуть раньше даты отчёта: видно и сделанное, и ближайшее
    el.scrollLeft = Math.max(
      0,
      momentX(anchor!, originDay, pxDay) - Math.min(el.clientWidth / 3, 21 * pxDay) - 40,
    );
  }, [view, zoom, originDay, pxDay]);

  const first = Math.max(0, Math.floor((scroll.top - 0) / RH) - 5);
  const last = Math.min(rows.length, Math.ceil((scroll.top + scroll.h) / RH) + 5);
  const visible = rows.slice(first, last);
  // Видимая часть шкалы в её собственных координатах (таблица слева закрывает начало)
  const xFrom = scroll.left - 200;
  const xTo = scroll.left + scroll.w - leftW + 200;
  const rowIndex = useMemo(() => new Map(rows.map((r, i) => [r.id, i])), [rows]);
  const actById = useMemo(() => new Map(view.activities.map((a) => [a.id, a])), [view.activities]);

  /* ── Перетаскивание ── */
  const [drag, setDrag] = useState<Drag | null>(null);
  const dragRef = useRef<Drag | null>(null);
  dragRef.current = drag;
  const xToMoment = useCallback(
    (x: number, snapHour: boolean) => {
      const dayF = x / pxDay;
      const d = originDay + Math.floor(dayF);
      let min = Math.round(((dayF - Math.floor(dayF)) * 1440) / 60) * 60;
      if (!snapHour) min = 0;
      if (min >= 1440) return `${dayString(d + 1)}T00:00`;
      return `${dayString(d)}T${String(Math.floor(min / 60)).padStart(2, '0')}:00`;
    },
    [originDay, pxDay],
  );
  const finishDrag = useCallback(
    (e: PointerEvent) => {
      const d = dragRef.current;
      setDrag(null);
      if (!d) return;
      const a = actById.get(d.id);
      if (!a?.dates) return;
      const hours = zoom === 'hours';
      if (d.kind === 'move' && Math.abs(d.dx) > 3) {
        const x = momentX(a.dates.es, originDay, pxDay) + d.dx;
        const c = calFor(a.calendarId);
        if (!c) return;
        // Начало — к ближайшему рабочему времени календаря работы
        const t = hours ? xToMoment(x, true) : xToMoment(x + pxDay / 2, false);
        const snapped = c.startAt(c.workAt(parseMoment(t)));
        const dd = dayString(Math.floor(snapped / 1440));
        const hh = String(Math.floor((snapped % 1440) / 60)).padStart(2, '0');
        const mm = String(snapped % 60).padStart(2, '0');
        onEdit(
          [
            {
              op: 'activity',
              id: a.id,
              data: { constraintType: 'snet', constraintDate: `${dd}T${hh}:${mm}` },
            },
          ],
          `Перенос ${a.code}: начало не ранее ${dd.split('-').reverse().join('.')}`,
        );
      }
      if (d.kind === 'resize' && Math.abs(d.dx) > 3 && a.type !== 'start_ms' && a.type !== 'finish_ms') {
        const x = momentX(a.dates.ef, originDay, pxDay) + d.dx;
        const c = calFor(a.calendarId);
        if (!c) return;
        const t = hours ? xToMoment(x, true) : `${xToMoment(x + pxDay / 2, false).slice(0, 10)}T00:00`;
        const w = c.workAt(parseMoment(t)) - c.workAt(parseMoment(a.dates.es));
        const step = hours ? 60 : c.hoursPerDay * 60;
        const dur = Math.max(60, Math.round(w / step) * step);
        if (dur !== a.durationMin)
          onEdit(
            [{ op: 'activity', id: a.id, data: { durationMin: dur } }],
            `Длительность ${a.code}: ${fmtDur(dur, c.hoursPerDay)}`,
          );
      }
      if (d.kind === 'link') {
        const el = scroller.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const y = e.clientY - r.top + el.scrollTop - HEADER;
        const target = rows[Math.floor(y / RH)];
        if (target?.kind === 'act' && target.id !== a.id) {
          const exists = view.relations.some((x) => x.predId === a.id && x.succId === target.id);
          if (!exists)
            onEdit(
              [
                {
                  op: 'relation',
                  id: crypto.randomUUID(),
                  create: true,
                  predId: a.id,
                  succId: target.id,
                  type: 'FS',
                  lagMin: 0,
                },
              ],
              `Связь ${a.code} → ${target.a.code}`,
            );
        }
      }
    },
    [actById, zoom, originDay, pxDay, calFor, xToMoment, onEdit, rows, RH, view.relations],
  );
  useEffect(() => {
    if (!drag) return;
    const move = (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      if (d.kind === 'link') {
        const el = scroller.current!;
        const r = el.getBoundingClientRect();
        setDrag({
          ...d,
          x: e.clientX - r.left + el.scrollLeft - leftW,
          y: e.clientY - r.top + el.scrollTop - HEADER,
        });
      } else setDrag({ ...d, dx: e.clientX - d.x0 });
    };
    const up = (e: PointerEvent) => finishDrag(e);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up, { once: true });
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, [drag, finishDrag, leftW]);

  const startDrag = (
    e: RPointerEvent,
    a: ActivityRow,
    kind: 'move' | 'resize' | 'link',
    bx: number,
    by: number,
  ) => {
    if (!canEdit || e.button !== 0 || a.lockedBy) return;
    if (kind !== 'link' && a.actualStart) return;
    e.stopPropagation();
    e.preventDefault();
    onSelect(a.id);
    if (kind === 'link') setDrag({ kind, id: a.id, x0: bx, y0: by, x: bx, y: by });
    else setDrag({ kind, id: a.id, x0: e.clientX, dx: 0 });
  };

  /* ── Шкала ── */
  const scale = useMemo(() => {
    const top: Array<{ x: number; w: number; label: string; off?: boolean }> = [];
    const bottom: Array<{ x: number; w: number; label: string; off?: boolean }> = [];
    const dFrom = Math.max(0, Math.floor(xFrom / pxDay) - 40);
    const dTo = Math.min(days, Math.ceil(xTo / pxDay) + 40);
    const push = (arr: typeof top, a: number, b: number, label: string, off?: boolean) =>
      arr.push({ x: a * pxDay, w: (b - a) * pxDay, label, off });
    for (let i = dFrom; i < dTo; ) {
      const ds = dayString(originDay + i);
      const y = Number(ds.slice(0, 4));
      const m = Number(ds.slice(5, 7));
      if (zoom === 'hours' || zoom === 'days') {
        const wd = (((originDay + i + 3) % 7) + 7) % 7;
        if (zoom === 'hours') {
          push(top, i, i + 1, `${ds.slice(8, 10)}.${ds.slice(5, 7)}.${ds.slice(2, 4)}`);
          for (let hh = 0; hh < 24; hh += 2)
            bottom.push({
              x: (i + hh / 24) * pxDay,
              w: pxDay / 12,
              label: String(hh),
              off: !cal?.isWorkDay(originDay + i),
            });
          i++;
        } else {
          if (wd === 0 || i === dFrom) {
            const end = i + (7 - wd);
            push(top, i, end, `${ds.slice(8, 10)} ${MONTHS[m - 1]} ${y}`);
          }
          push(bottom, i, i + 1, String(Number(ds.slice(8, 10))), !cal?.isWorkDay(originDay + i));
          i++;
        }
      } else if (zoom === 'weeks') {
        const monthStart = dayNumber(`${y}-${String(m).padStart(2, '0')}-01`) - originDay;
        const next =
          dayNumber(m === 12 ? `${y + 1}-01-01` : `${y}-${String(m + 1).padStart(2, '0')}-01`) - originDay;
        if (i === dFrom || i === monthStart)
          push(top, Math.max(i, monthStart), next, `${MONTHS[m - 1]} ${y}`);
        const wd = (((originDay + i + 3) % 7) + 7) % 7;
        if (wd === 0) push(bottom, i, i + 7, String(Number(ds.slice(8, 10))));
        i++;
      } else {
        const next =
          dayNumber(m === 12 ? `${y + 1}-01-01` : `${y}-${String(m + 1).padStart(2, '0')}-01`) - originDay;
        const q = Math.floor((m - 1) / 3);
        if (zoom === 'months') {
          if (m === 1 || i === dFrom) push(top, i, dayNumber(`${y + 1}-01-01`) - originDay, String(y));
          push(bottom, i, next, MONTHS[m - 1]!);
        } else {
          if (m === 1 || i === dFrom) push(top, i, dayNumber(`${y + 1}-01-01`) - originDay, String(y));
          if ((m - 1) % 3 === 0 || i === dFrom) {
            const qEnd =
              dayNumber(q === 3 ? `${y + 1}-01-01` : `${y}-${String(q * 3 + 4).padStart(2, '0')}-01`) -
              originDay;
            push(bottom, i, qEnd, `${q + 1} кв`);
          }
        }
        i = Math.max(i + 1, next);
      }
    }
    return { top, bottom };
  }, [xFrom, xTo, pxDay, days, originDay, zoom, cal]);

  const dataX = view.schedule.dataDate ? momentX(view.schedule.dataDate, originDay, pxDay) : null;
  const today = new Date();
  const todayX = momentX(
    `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}T${String(today.getHours()).padStart(2, '0')}:00`,
    originDay,
    pxDay,
  );
  const bodyH = rows.length * RH;

  /* ── Полосы ── */
  const bar = (r: Row, i: number) => {
    const y = i * RH;
    if (r.kind !== 'act') {
      if (!r.start || !r.finish) return null;
      const x1 = momentX(r.start, originDay, pxDay);
      const x2 = momentX(r.finish, originDay, pxDay);
      if (x2 < xFrom || x1 > xTo) return null;
      const by = y + RH / 2 - 3;
      return (
        <g key={r.id}>
          <rect
            x={x1}
            y={by}
            width={Math.max(2, x2 - x1)}
            height={6}
            fill={r.critical ? 'var(--color-danger)' : 'var(--color-ink)'}
          />
          <path
            d={`M${x1} ${by} v10 l5 -4 Z M${x2} ${by} v10 l-5 -4 Z`}
            fill={r.critical ? 'var(--color-danger)' : 'var(--color-ink)'}
          />
        </g>
      );
    }
    const a = r.a;
    const { start, finish } = barDates(a);
    if (!start || !finish) return null;
    let x1 = momentX(start, originDay, pxDay);
    let x2 = momentX(finish, originDay, pxDay);
    if (drag && drag.id === a.id && drag.kind === 'move') {
      x1 += drag.dx;
      x2 += drag.dx;
    }
    if (drag && drag.id === a.id && drag.kind === 'resize') x2 = Math.max(x1 + 2, x2 + drag.dx);
    const bl = showBaseline ? view.baselineDates[a.id] : undefined;
    const blx1 = bl ? momentX(bl[0], originDay, pxDay) : 0;
    const blx2 = bl ? momentX(bl[1], originDay, pxDay) : 0;
    if (Math.max(x2, blx2) < xFrom || Math.min(x1, bl ? blx1 : x1) > xTo) return null;
    const crit = a.dates?.critical;
    const near = a.dates?.nearCritical;
    const fill = crit ? 'var(--color-danger)' : near ? 'var(--color-warning)' : 'var(--color-ink-2)';
    const ms = a.type === 'start_ms' || a.type === 'finish_ms';
    const sel = selectedId === a.id;
    const by = y + (bl ? 5 : 8);
    const bh = RH - (bl ? 16 : 16);
    const label = `${a.code} ${a.name}\n${fmtMoment(start, true)} — ${fmtMoment(finish, true)}\nДлительность ${fmtDur(a.durationMin, hpd(view, a.calendarId))}${a.dates?.tf != null ? `, резерв ${fmtDur(a.dates.tf, hpd(view, a.calendarId))}` : ''}`;
    // Резерв — тонкая линия от окончания до позднего окончания
    const tfx = a.dates?.tf && a.dates.tf > 0 && !ms ? momentX(a.dates.lf, originDay, pxDay) : null;
    const pct = a.actualFinish ? 100 : a.percent;
    return (
      <g key={a.id} data-act={a.id}>
        {bl ? (
          <rect
            x={blx1}
            y={y + RH - 9}
            width={Math.max(2, blx2 - blx1)}
            height={4}
            fill="var(--color-line-strong)"
            opacity={0.7}
          />
        ) : null}
        {tfx !== null && tfx > x2 ? (
          <line
            x1={x2}
            x2={tfx}
            y1={by + bh / 2}
            y2={by + bh / 2}
            stroke="var(--color-line-strong)"
            strokeDasharray="2 3"
          />
        ) : null}
        {ms ? (
          <path
            d={`M${x1} ${y + RH / 2 - 7} l7 7 l-7 7 l-7 -7 Z`}
            fill={a.actualFinish ? 'var(--color-ink)' : fill}
            stroke={sel ? 'var(--color-accent)' : 'none'}
            strokeWidth={2}
            onPointerDown={(e) => startDrag(e, a, 'move', x1, y + RH / 2)}
            className={canEdit && !a.actualStart ? 'cursor-grab' : undefined}
          >
            <title>{label}</title>
          </path>
        ) : (
          <>
            <rect
              x={x1}
              y={by}
              width={Math.max(2, x2 - x1)}
              height={bh}
              rx={2}
              fill={fill}
              opacity={a.type === 'loe' || a.type === 'hammock' ? 0.35 : 1}
              stroke={sel ? 'var(--color-accent)' : 'none'}
              strokeWidth={2}
              onPointerDown={(e) => startDrag(e, a, 'move', x2, y + RH / 2)}
              className={canEdit && !a.actualStart ? 'cursor-grab' : undefined}
            >
              <title>{label}</title>
            </rect>
            {pct > 0 ? (
              <rect
                x={x1}
                y={by + bh / 2 - 2}
                width={Math.max(1, ((x2 - x1) * pct) / 100)}
                height={4}
                fill="var(--color-on-ink)"
                opacity={0.9}
                pointerEvents="none"
              />
            ) : null}
            {canEdit && !a.actualStart && a.type === 'task' ? (
              <rect
                x={x2 - 5}
                y={by}
                width={8}
                height={bh}
                fill="transparent"
                className="cursor-ew-resize"
                onPointerDown={(e) => startDrag(e, a, 'resize', x2, y)}
              />
            ) : null}
          </>
        )}
        {canEdit ? (
          <circle
            cx={x2 + (ms ? 10 : 7)}
            cy={y + RH / 2}
            r={4}
            fill="var(--color-surface)"
            stroke="var(--color-ink-2)"
            className={clsx('cursor-crosshair', sel ? 'opacity-100' : 'opacity-0 hover:opacity-100')}
            onPointerDown={(e) => startDrag(e, a, 'link', x2 + 7, y + RH / 2)}
          >
            <title>Протяните к другой работе, чтобы связать окончание-начало</title>
          </circle>
        ) : null}
        {!ms && x2 - x1 < 160 && zoom !== 'quarters' ? (
          <text x={x2 + 16} y={y + RH / 2 + 4} fontSize={11} fill="var(--color-ink-2)" pointerEvents="none">
            {a.name.length > 40 ? `${a.name.slice(0, 38)}…` : a.name}
          </text>
        ) : null}
        {ms ? (
          <text x={x1 + 12} y={y + RH / 2 + 4} fontSize={11} fill="var(--color-ink)" pointerEvents="none">
            {fmtMoment(start)} {a.name.length > 40 ? `${a.name.slice(0, 38)}…` : a.name}
          </text>
        ) : null}
      </g>
    );
  };

  /* ── Связи ── */
  const arrows = useMemo(() => {
    if (!showLinks) return [];
    const out: Array<{ id: string; d: string; crit: boolean; driving: boolean }> = [];
    for (const r of view.relations) {
      const pi = rowIndex.get(r.predId);
      const si = rowIndex.get(r.succId);
      if (pi === undefined || si === undefined) continue;
      if (Math.max(pi, si) < first || Math.min(pi, si) > last) continue;
      const p = actById.get(r.predId);
      const s = actById.get(r.succId);
      if (!p || !s) continue;
      const pd = barDates(p);
      const sd = barDates(s);
      if (!pd.start || !pd.finish || !sd.start || !sd.finish) continue;
      const fromFinish = r.type === 'FS' || r.type === 'FF';
      const toStart = r.type === 'FS' || r.type === 'SS';
      const x1 = momentX(fromFinish ? pd.finish : pd.start, originDay, pxDay);
      const x2 = momentX(toStart ? sd.start : sd.finish, originDay, pxDay);
      if (Math.max(x1, x2) < xFrom || Math.min(x1, x2) > xTo) continue;
      const y1 = pi * RH + RH / 2;
      const y2 = si * RH + RH / 2;
      const out1 = fromFinish ? x1 + 6 : x1 - 6;
      let d: string;
      if (toStart && x2 - 6 >= out1) d = `M${x1} ${y1} H${x2 - 3} V${y2 + (y2 > y1 ? -6 : 6)}`;
      else if (toStart) {
        const mid = y2 + (y2 > y1 ? -RH / 2 : RH / 2);
        d = `M${x1} ${y1} H${out1} V${mid} H${x2 - 8} V${y2} H${x2 - 1}`;
      } else {
        const mid = Math.max(x1, x2) + 8;
        d = `M${x1} ${y1} H${fromFinish ? mid : out1} V${y2} H${x2 + 1}`;
      }
      out.push({
        id: r.id,
        d,
        crit: Boolean(p.dates?.critical && s.dates?.critical && r.driving),
        driving: r.driving,
      });
    }
    return out;
  }, [showLinks, view.relations, rowIndex, actById, first, last, originDay, pxDay, xFrom, xTo, RH]);

  /* ── Ячейки таблицы ── */
  const cell = (r: Row, key: string) => {
    if (r.kind !== 'act') {
      if (key === 'name')
        return (
          <button
            type="button"
            className="flex h-full min-h-full w-full min-w-0 items-center gap-1 text-left font-semibold"
            style={{ paddingLeft: 4 + r.depth * 14 }}
            onClick={() => onToggle(r.id)}
            aria-expanded={!r.collapsed}
          >
            {r.collapsed ? (
              <ChevronRight className="size-4 shrink-0" />
            ) : (
              <ChevronDown className="size-4 shrink-0" />
            )}
            <span className="truncate">
              {r.kind === 'wbs' && r.code ? `${r.code} ` : ''}
              {r.name}
            </span>
          </button>
        );
      if (key === 'start') return fmtMoment(r.start);
      if (key === 'finish') return fmtMoment(r.finish);
      return null;
    }
    const a = r.a;
    const h = hpd(view, a.calendarId);
    const { start, finish } = barDates(a);
    switch (key) {
      case 'code':
        return <span className="text-ink-2">{a.code}</span>;
      case 'name':
        return (
          <span className="flex min-w-0 items-center gap-1" style={{ paddingLeft: 4 + r.depth * 14 + 18 }}>
            {a.lockedBy ? (
              <Lock className="size-3.5 shrink-0 text-warning" aria-label={`Правит ${a.lockedBy.name}`} />
            ) : null}
            <span className={clsx('truncate', a.dates?.critical && 'text-danger')}>
              {phone ? <span className="mr-1 text-[11px] text-ink-2">{a.code}</span> : null}
              {a.name}
            </span>
          </span>
        );
      case 'dur':
        return a.type === 'start_ms' || a.type === 'finish_ms' ? '0' : fmtDur(a.durationMin, h);
      case 'start':
        return (
          <span>
            {fmtMoment(start)}
            {a.actualStart ? ' ф' : ''}
          </span>
        );
      case 'finish':
        return (
          <span>
            {fmtMoment(finish)}
            {a.actualFinish ? ' ф' : ''}
          </span>
        );
      case 'tf':
        return a.dates?.tf == null ? (
          '—'
        ) : (
          <span className={clsx(a.dates.tf < 0 && 'text-danger')}>{fmtDur(a.dates.tf, h)}</span>
        );
      default:
        return null;
    }
  };

  return (
    <section
      ref={scroller}
      className="relative overflow-auto overscroll-contain rounded-lg border border-line bg-surface select-none"
      style={{ height }}
      aria-label="Диаграмма Ганта"
    >
      <div className="relative" style={{ width: leftW + timelineW, height: HEADER + bodyH }}>
        {/* Заголовок: столбцы и шкала */}
        <div className="sticky top-0 z-20 flex" style={{ height: HEADER, width: leftW + timelineW }}>
          <div
            className="sticky left-0 z-30 flex border-r border-b border-line bg-surface-2 text-[12px] font-medium text-ink-2"
            style={{ width: leftW }}
          >
            {cols.map((c) => (
              <div key={c.key} className="flex items-end px-2 pb-2" style={{ width: c.w }}>
                {c.title}
              </div>
            ))}
          </div>
          <svg
            width={timelineW}
            height={HEADER}
            className="block border-b border-line bg-surface-2"
            aria-hidden
          >
            {scale.top.map((t) => (
              <g key={`t${t.x}`}>
                <line x1={t.x} x2={t.x} y1={0} y2={HEADER / 2} stroke="var(--color-line)" />
                <text
                  x={Math.max(t.x, Math.min(scroll.left, t.x + t.w - 90)) + 4}
                  y={16}
                  fontSize={11}
                  fill="var(--color-ink)"
                >
                  {t.label}
                </text>
              </g>
            ))}
            <line x1={0} x2={timelineW} y1={HEADER / 2} y2={HEADER / 2} stroke="var(--color-line)" />
            {scale.bottom.map((t) => (
              <g key={`b${t.x}`}>
                {t.off ? (
                  <rect x={t.x} y={HEADER / 2} width={t.w} height={HEADER / 2} fill="var(--color-tile)" />
                ) : null}
                <line x1={t.x} x2={t.x} y1={HEADER / 2} y2={HEADER} stroke="var(--color-line)" />
                {t.w >= 14 ? (
                  <text x={t.x + 3} y={HEADER - 8} fontSize={10} fill="var(--color-ink-2)">
                    {t.label}
                  </text>
                ) : null}
              </g>
            ))}
          </svg>
        </div>

        {/* Таблица слева: закреплена при прокрутке вбок */}
        <div className="sticky left-0 z-10" style={{ width: leftW, height: bodyH }}>
          <div style={{ transform: `translateY(${first * RH}px)` }}>
            {visible.map((r) => (
              <div
                key={r.id}
                className={clsx(
                  'flex border-r border-b border-line text-[13px]',
                  r.kind === 'act' ? 'bg-surface' : 'bg-surface-2',
                  r.id === selectedId && 'bg-accent-soft',
                )}
                style={{ height: RH, width: leftW }}
              >
                {cols.map((c) => (
                  <div
                    key={c.key}
                    className={clsx(
                      'flex min-w-0 overflow-hidden px-2 whitespace-nowrap',
                      // Название — кнопка на всю высоту строки: на телефоне нажатие не меньше 44 px
                      c.key === 'name' ? 'items-stretch' : 'items-center',
                      c.key !== 'name' && 'num justify-end text-right',
                    )}
                    style={{ width: c.w }}
                  >
                    {c.key === 'name' && r.kind === 'act' ? (
                      <button
                        type="button"
                        className="flex min-w-0 flex-1 items-center text-left"
                        onClick={() => {
                          onSelect(r.id);
                          onOpen(r.id);
                        }}
                        title={`${r.a.code} ${r.a.name}`}
                      >
                        {cell(r, c.key)}
                      </button>
                    ) : (
                      cell(r, c.key)
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Сетка, выходные, линии дат, полосы и связи */}
        <svg
          className="absolute"
          style={{ left: leftW, top: HEADER }}
          width={timelineW}
          height={Math.max(bodyH, 1)}
        >
          <defs>
            <marker
              id="gantt-arrow"
              viewBox="0 0 6 6"
              refX="5"
              refY="3"
              markerWidth="6"
              markerHeight="6"
              orient="auto-start-reverse"
            >
              <path d="M0 0 L6 3 L0 6 Z" fill="var(--color-ink-2)" />
            </marker>
            <marker
              id="gantt-arrow-crit"
              viewBox="0 0 6 6"
              refX="5"
              refY="3"
              markerWidth="6"
              markerHeight="6"
              orient="auto-start-reverse"
            >
              <path d="M0 0 L6 3 L0 6 Z" fill="var(--color-danger)" />
            </marker>
          </defs>
          {(zoom === 'days' || zoom === 'hours') && cal
            ? scale.bottom
                .filter((t) => t.off)
                .map((t) => (
                  <rect
                    key={`o${t.x}`}
                    x={t.x}
                    y={first * RH}
                    width={t.w}
                    height={(last - first) * RH}
                    fill="var(--color-surface-2)"
                    opacity={0.6}
                  />
                ))
            : null}
          {visible.map((r, k) =>
            r.id === selectedId ? (
              <rect
                key="sel"
                x={0}
                y={(first + k) * RH}
                width={timelineW}
                height={RH}
                fill="var(--color-accent-soft)"
                opacity={0.5}
              />
            ) : null,
          )}
          {todayX > xFrom && todayX < xTo ? (
            <line
              x1={todayX}
              x2={todayX}
              y1={first * RH}
              y2={last * RH}
              stroke="var(--color-line-strong)"
              strokeDasharray="1 3"
            />
          ) : null}
          {dataX !== null ? (
            <line
              x1={dataX}
              x2={dataX}
              y1={first * RH}
              y2={last * RH}
              stroke="var(--color-accent)"
              strokeWidth={2}
              strokeDasharray="6 4"
            >
              <title>Дата отчёта {fmtMoment(view.schedule.dataDate, true)}</title>
            </line>
          ) : null}
          {arrows.map((a) => (
            <path
              key={a.id}
              d={a.d}
              fill="none"
              stroke={a.crit ? 'var(--color-danger)' : 'var(--color-ink-2)'}
              strokeWidth={1}
              strokeDasharray={a.driving ? undefined : '3 2'}
              markerEnd={a.crit ? 'url(#gantt-arrow-crit)' : 'url(#gantt-arrow)'}
              opacity={0.8}
            />
          ))}
          {visible.map((r, k) => bar(r, first + k))}
          {drag?.kind === 'link' ? (
            <line
              x1={drag.x0}
              y1={drag.y0}
              x2={drag.x}
              y2={drag.y}
              stroke="var(--color-accent)"
              strokeWidth={2}
            />
          ) : null}
        </svg>
      </div>
    </section>
  );
}
